import { z } from 'zod';

/**
 * Persönliche Jahresübersicht (Self-Service, Recht `timetracking.read`) — die
 * eigenen Stunden eines Kalenderjahres, grafisch aufbereitet. Im Unterschied
 * zur Admin-Analyse (`analytics.schema.ts`, Recht `timetracking.manage`,
 * tenant-weit über alle Mitarbeiter) zeigt diese Übersicht ausschließlich die
 * eigenen Buchungen — der Mitarbeiter wird serverseitig aus dem angemeldeten
 * User aufgelöst, nicht aus einem Query-Parameter (wie beim übrigen
 * Self-Service der Zeiterfassung).
 *
 * Bewusst OHNE Kosten: die Admin-Analyse blendet die Kosten-Spalte per Default
 * aus, weil Kosten ÷ Stunden den Stundensatz verrät (Tech-Lead-Entscheid,
 * s. Moduldoku) — dieselbe Zurückhaltung gilt erst recht für die eigene Sicht.
 */
export const myYearOverviewQuerySchema = z.object({
  /** Kalenderjahr (Berlin) — ohne Angabe das laufende Jahr. */
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type MyYearOverviewQuery = z.infer<typeof myYearOverviewQuerySchema>;

/** Ein Monatspunkt des Stunden-Verlaufs. */
export const myYearOverviewMonthPointSchema = z.object({
  /** `JJJJ-MM` (chronologisch sortierbar). */
  month: z.string(),
  hours: z.number(),
});
export type MyYearOverviewMonthPoint = z.infer<typeof myYearOverviewMonthPointSchema>;

/** Eine Aufschlüsselungs-Zeile (Projekt oder Kategorie) — ohne Kosten. */
export const myYearOverviewBreakdownSchema = z.object({
  label: z.string(),
  /** Zweitzeile (z. B. Gesellschaft beim Projekt) — optional. */
  subLabel: z.string().nullable(),
  hours: z.number(),
});
export type MyYearOverviewBreakdown = z.infer<typeof myYearOverviewBreakdownSchema>;

export const myYearOverviewSchema = z.object({
  year: z.number().int(),
  totalHours: z.number(),
  /** Anzahl gezählter Buchungen im Jahr. */
  entryCount: z.number().int(),
  /** Zwölf Monatspunkte, lückenlos (Monate ohne Buchung = 0). */
  byMonth: z.array(myYearOverviewMonthPointSchema),
  byProject: z.array(myYearOverviewBreakdownSchema),
  byCategory: z.array(myYearOverviewBreakdownSchema),
  /**
   * Arbeitstage (kein Wochenende, kein Feiertag) bis heute ohne JEDE Erfassung
   * — weder Zeitbuchung noch genehmigter Urlaub/Krankheit/Arzttermin.
   * `JJJJ-MM-TT`, aufsteigend sortiert.
   */
  missingWorkingDays: z.array(z.string()),
});
export type MyYearOverview = z.infer<typeof myYearOverviewSchema>;
